import { useMemo, useState } from "react";
import "./StateDistributionChart.css";

// Ranked horizontal bars for "how many X per state".
//
// The vertical bar chart this replaces put 30-50 states on a fixed 600px axis:
// bars overlapped, most state names were dropped by the axis, and the value
// labels collided into an unreadable smear. Magnitude across many long-named
// categories is a ranking, so the bars go horizontal (name has its own column
// and can't collide), sorted high-to-low, and the list scrolls instead of
// compressing. Length carries magnitude; one hue throughout, because rank is
// already encoded by position and a second encoding would be noise.
const MIN_BAR_PERCENT = 0.75; // keeps a nonzero count from rendering as nothing

// `total` — pass it when one entity can appear in several states (a provider is
// licensed in many), because then the buckets overlap and adding them up counts
// the same person once per state. Left off, the total is the sum, which is
// right only for disjoint buckets like patients, who live in one state.
export const StateDistributionChart = ({ data = [], unit = "users", total }) => {
  const [query, setQuery] = useState("");
  const overlapping = typeof total === "number";

  const { rows, summed, max } = useMemo(() => {
    // Backend sends every active state, most with nothing in them; an empty bar
    // is not a data point and it inflates the "across N states" count.
    const cleaned = data
      .filter((d) => d?.name)
      .map((d) => ({ name: d.name, count: Number(d.count) || 0 }))
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);
    return {
      rows: cleaned,
      summed: cleaned.reduce((sum, d) => sum + d.count, 0),
      max: cleaned.length ? cleaned[0].count : 0,
    };
  }, [data]);

  const headlineTotal = overlapping ? total : summed;

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? rows.filter((r) => r.name.toLowerCase().includes(needle))
    : rows;

  if (!rows.length) {
    return <p className="state-dist__empty">No state data available.</p>;
  }

  return (
    <div className="state-dist">
      <div className="state-dist__summary">
        <span>
          <strong>{headlineTotal.toLocaleString()}</strong> {unit}{" "}
          {overlapping ? "licensed across" : "across"}{" "}
          <strong>{rows.length}</strong> states
        </span>
        {rows.length > 8 && (
          <input
            type="search"
            className="state-dist__filter"
            placeholder="Filter states"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={`Filter ${unit} by state`}
          />
        )}
      </div>

      <ol className="state-dist__list">
        {visible.map((row) => {
          const share = summed ? (row.count / summed) * 100 : 0;
          const width = max
            ? Math.max((row.count / max) * 100, row.count ? MIN_BAR_PERCENT : 0)
            : 0;
          // With overlapping buckets a share-of-total percentage is nonsense
          // (the shares would sum well past 100%), so state the fraction of the
          // roster instead.
          const detail = overlapping
            ? `${row.count.toLocaleString()} of ${headlineTotal.toLocaleString()} ${unit}`
            : `${row.count.toLocaleString()} ${unit} (${share.toFixed(1)}% of total)`;
          return (
            <li
              key={row.name}
              className="state-dist__row"
              title={`${row.name}: ${detail}`}
            >
              <span className="state-dist__name">{row.name}</span>
              <span className="state-dist__track">
                <span
                  className="state-dist__bar"
                  style={{ width: `${width}%` }}
                />
              </span>
              <span className="state-dist__value">
                {row.count.toLocaleString()}
              </span>
            </li>
          );
        })}
        {!visible.length && (
          <li className="state-dist__empty">No states match “{query}”.</li>
        )}
      </ol>
    </div>
  );
};
